/**
 * Dashboard Page Component
 * 
 * This is a placeholder page component for the Dashboard route.
 * It will be replaced with the actual dashboard implementation.
 * 
 * Features:
 * - Placeholder content
 * - Accessibility compliance
 * - Responsive design
 * - Ready for React component integration
 * 
 * @see .specify/memory/constitution.md
 * @see /Reference/Reference design-review/design-principles.md
 */

import React from 'react';
import { format, startOfMonth, endOfMonth, subMonths, addMonths, isSameMonth } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { SpendingByCategoryCard } from './dashboard/components/SpendingByCategoryCard';
import { SpendingTrendsByCategoryCard } from './dashboard/components/SpendingTrendsByCategoryCard';
import { RecentTransactionsCard } from './dashboard/components/RecentTransactionsCard';
import { type DateRange } from './dashboard/components/DateRangeSelector';
import { StatCard } from './dashboard/components/DashboardStatsCard';
import { usePreferences } from '../hooks/usePreferences';
import { useDashboardStats } from '../hooks/useDashboardStats';
import { useRegion } from '../contexts/RegionContext';
import { PageHeader } from '../components/PageHeader';
import { getCurrentUserId } from '../lib/supabase';
import { supabaseAccountService } from '../services/supabase-service';
import { categoryService } from '../services/category-service';
import { AccountType } from '../types/account.types';
import { CategoryType } from '../types/category.types';

/**
 * Dashboard Page Component
 * 
 * Placeholder for the main dashboard page.
 */
export function DashboardPage(): React.ReactElement {
  // State for spending charts date range (separate from Financial Overview stats)
  const [chartDateRange, setChartDateRange] = React.useState<DateRange | undefined>();
  const { data: preferences } = usePreferences();
  const { formatCurrency } = useRegion();
  const [userId, setUserId] = React.useState<string | null>(null);
  
  React.useEffect(() => {
    getCurrentUserId().then(setUserId);
  }, []);
  
  // State for selected month in Financial Overview
  const [selectedMonth, setSelectedMonth] = React.useState<Date>(() => {
    const now = new Date();
    return subMonths(now, 1); // Default to last month
  });
  
  // Financial Overview stats (uses selected month)
  const { stats, isLoading: isLoadingStats } = useDashboardStats(selectedMonth);
  
  // Calculate date range for selected month
  const selectedMonthDateRange = React.useMemo(() => {
    return {
      from: format(startOfMonth(selectedMonth), 'yyyy-MM-dd'),
      to: format(endOfMonth(selectedMonth), 'yyyy-MM-dd'),
    };
  }, [selectedMonth]);
  
  // Fetch savings account IDs for the savings card link
  const { data: savingsAccounts } = useQuery({
    queryKey: ['savings-accounts', userId],
    queryFn: async () => {
      if (!userId) return [];
      const result = await supabaseAccountService.getAccounts(userId, { status: 'active' });
      return result.accounts
        .filter(acc => acc.type === AccountType.SAVINGS)
        .map(acc => acc.id);
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Fetch income category IDs for the income card link
  const { data: incomeCategoryIds } = useQuery({
    queryKey: ['income-categories', userId],
    queryFn: async () => {
      if (!userId) return [];
      const result = await categoryService.listCategories({ type: CategoryType.INCOME });
      if (result.error) {
        console.error('Error fetching income categories:', result.error);
        return [];
      }
      return result.data?.categories.map(cat => cat.id) || [];
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Fetch Everyday and Liability account IDs for the expenses card link
  // Expenses are calculated from BANK_CASH, CREDIT_CARD, and LOAN accounts only
  const { data: expenseAccountIds } = useQuery({
    queryKey: ['expense-accounts', userId],
    queryFn: async () => {
      if (!userId) return [];
      const result = await supabaseAccountService.getAccounts(userId, { status: 'active' });
      return result.accounts
        .filter(acc => 
          acc.type === AccountType.BANK_CASH || 
          acc.type === AccountType.CREDIT_CARD || 
          acc.type === AccountType.LOAN
        )
        .map(acc => acc.id);
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Fetch expense category IDs that are NOT excluded from spending
  // This matches the expense calculation which excludes uncategorized and excluded categories
  const { data: expenseCategoryIds } = useQuery({
    queryKey: ['expense-categories-not-excluded', userId],
    queryFn: async () => {
      if (!userId) return [];
      // Fetch all expense categories
      const result = await categoryService.listCategories({ type: CategoryType.EXPENSE });
      if (result.error) {
        console.error('Error fetching expense categories:', result.error);
        return [];
      }
      
      const allCategories = result.data?.categories || [];
      
      // Build a map of category ID to category info (including parent info)
      const categoryMap = new Map<string, { excludeFromSpending: boolean; parentId?: string }>();
      allCategories.forEach(cat => {
        categoryMap.set(cat.id, {
          excludeFromSpending: cat.excludeFromSpending || false,
          parentId: cat.parentId
        });
      });
      
      // Filter out excluded categories
      // Also exclude categories whose parent is excluded
      const nonExcludedCategoryIds = allCategories
        .filter(cat => {
          // Check if this category is excluded
          if (cat.excludeFromSpending) {
            return false;
          }
          
          // Check if parent category is excluded
          if (cat.parentId) {
            const parent = categoryMap.get(cat.parentId);
            if (parent?.excludeFromSpending) {
              return false;
            }
          }
          
          return true;
        })
        .map(cat => cat.id);
      
      return nonExcludedCategoryIds;
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
  
  // Check if selected month is current month
  const isCurrentMonth = React.useMemo(() => {
    const now = new Date();
    return isSameMonth(selectedMonth, now);
  }, [selectedMonth]);
  
  const handlePreviousMonth = () => {
    setSelectedMonth(subMonths(selectedMonth, 1));
  };
  
  const handleNextMonth = () => {
    if (!isCurrentMonth) {
      setSelectedMonth(addMonths(selectedMonth, 1));
    }
  };

  const handleChartDateRangeChange = (range: DateRange) => {
    setChartDateRange(range);
  };

  // Default date range for charts only - defaults to last month
  const effectiveChartDateRange = React.useMemo(() => {
    if (chartDateRange?.from && chartDateRange?.to) {
      return chartDateRange;
    }
    // Default to last month for charts - use date-fns for consistency
    const now = new Date();
    const lastMonth = subMonths(now, 1);
    return {
      from: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
      to: format(endOfMonth(lastMonth), 'yyyy-MM-dd'),
    };
  }, [chartDateRange]);

  // Get dashboard preferences with defaults (all true if not set)
  const dashboardPrefs = preferences?.dashboardPreferences || {
    showSpendingChart: true,
    showRecentTransactions: true,
    showBudgetOverview: true,
  };

  const formatChange = (change?: number): string | undefined => {
    if (change === undefined) return undefined;
    const sign = change > 0 ? '+' : '';
    // Format to maximum 2 decimal places, removing trailing zeros
    const formatted = Math.abs(change).toFixed(2).replace(/\.?0+$/, '');
    return `${sign}${formatted}%`;
  };

  // Format month name for titles (e.g., "November")
  const monthName = format(selectedMonth, 'MMMM');

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title="Dashboard"
        description="Overview of your financial situation and recent activity"
        helpSlug="dashboard"
      />

      {/* Financial Stats Cards */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Financial Overview
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePreviousMonth}
              className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            </button>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[120px] text-center">
              {format(selectedMonth, 'MMMM yyyy')}
            </span>
            <button
              onClick={handleNextMonth}
              disabled={isCurrentMonth}
              className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </div>
        <div className="main-page-grid-4 gap-6">
          {/* 1. Net Worth - Total financial position */}
          <StatCard
            title="Current Total Net Worth"
            value={
              isLoadingStats
                ? '...'
                : stats
                ? formatCurrency(stats.netWorth.value)
                : formatCurrency(0)
            }
            change={
              stats?.netWorth.change !== undefined
                ? formatChange(stats.netWorth.change)
                : undefined
            }
            changeType={stats?.netWorth.changeType}
            icon="chart"
            isLoading={isLoadingStats}
            cardType="networth"
          />
          {/* 2. Monthly Income - Money coming in */}
          <StatCard
            title={`${monthName} Income`}
            value={
              isLoadingStats
                ? '...'
                : stats
                ? formatCurrency(stats.monthlyIncome.value)
                : formatCurrency(0)
            }
            change={
              stats?.monthlyIncome.change !== undefined
                ? formatChange(stats.monthlyIncome.change)
                : undefined
            }
            changeType={stats?.monthlyIncome.changeType}
            icon="dollar"
            isLoading={isLoadingStats}
            transactionCount={stats?.monthlyIncome.transactionCount}
            cardType="income"
            dateFrom={selectedMonthDateRange.from}
            dateTo={selectedMonthDateRange.to}
            incomeCategoryIds={incomeCategoryIds}
          />
          {/* 3. Monthly Expenses - Money going out */}
          <StatCard
            title={`${monthName} Expenses`}
            value={
              isLoadingStats
                ? '...'
                : stats
                ? formatCurrency(stats.monthlyExpenses.value)
                : formatCurrency(0)
            }
            change={
              stats?.monthlyExpenses.change !== undefined
                ? formatChange(stats.monthlyExpenses.change)
                : undefined
            }
            changeType={stats?.monthlyExpenses.changeType}
            icon="cart"
            isLoading={isLoadingStats}
            transactionCount={stats?.monthlyExpenses.transactionCount}
            cardType="expenses"
            dateFrom={selectedMonthDateRange.from}
            dateTo={selectedMonthDateRange.to}
            expenseAccountIds={expenseAccountIds}
            expenseCategoryIds={expenseCategoryIds}
          />
          {/* 4. Monthly Savings - Net flow (income - expenses) for Savings accounts */}
          <StatCard
            title={`${monthName} Savings`}
            value={
              isLoadingStats
                ? '...'
                : stats
                ? formatCurrency(stats.monthlySavings.value)
                : formatCurrency(0)
            }
            change={
              stats?.monthlySavings.change !== undefined
                ? formatChange(stats.monthlySavings.change)
                : undefined
            }
            changeType={stats?.monthlySavings.changeType}
            icon="wallet"
            isLoading={isLoadingStats}
            transactionCount={stats?.monthlySavings.transactionCount}
            cardType="savings"
            dateFrom={selectedMonthDateRange.from}
            dateTo={selectedMonthDateRange.to}
            accountIds={savingsAccounts}
          />
        </div>
      </div>

      {/* Dashboard Components Grid */}
      {(dashboardPrefs.showSpendingChart || dashboardPrefs.showRecentTransactions) && (
        <div
          className={
            dashboardPrefs.showSpendingChart && dashboardPrefs.showRecentTransactions
              ? 'main-page-grid-3'
              : 'grid grid-cols-1 gap-6'
          }
        >
          {/* Spending Chart - Conditionally rendered */}
          {dashboardPrefs.showSpendingChart && (
            <SpendingByCategoryCard
              className={dashboardPrefs.showRecentTransactions ? 'main-page-col-span-2' : ''}
              dateFrom={effectiveChartDateRange.from}
              dateTo={effectiveChartDateRange.to}
              onDateRangeChange={handleChartDateRangeChange}
            />
          )}
          
          {/* Recent Transactions - Conditionally rendered */}
          {dashboardPrefs.showRecentTransactions && (
            <RecentTransactionsCard />
          )}
        </div>
      )}

      {/* Spending Trends by Category - Conditionally rendered */}
      {dashboardPrefs.showBudgetOverview && (
        <SpendingTrendsByCategoryCard />
      )}
    </div>
  );
}
